import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  VisibilityOff as VisibilityOffIcon,
  ArrowUpward,
  Search as SearchIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { env } from "../../../utilities/function";
import { getCookie } from "../../../utilities/cookies";
import ProductOfferForm from "./ProductOfferForm";

import RowPerPage from "../../../components/RowPerPage";
import Pagination from "../../../components/Pagination";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const API_BASE_URL = env("SERVER") + "/api";

const fetchOffers = async (page = 1, limit = 100, search = "", appType = "", isEnabled = "") => {
  const accessToken = getCookie("accessToken");
  const response = await axios.get(`${API_BASE_URL}/product-offers?page=${page}&limit=${limit}&search=${search}&appType=${appType}&isEnabled=${isEnabled}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data.data;
};

const deleteOffer = async (id) => {
  const accessToken = getCookie("accessToken");
  return await axios.delete(`${API_BASE_URL}/product-offers/${id}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

const toggleOfferStatus = async ({ id, isEnabled }) => {
  const accessToken = getCookie("accessToken");
  return await axios.patch(
    `${API_BASE_URL}/product-offers/${id}/toggle`,
    { isEnabled },
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
};

const ProductOffers = () => {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [offerToDelete, setOfferToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [appTypeFilter, setAppTypeFilter] = useState("");
  const [isEnabledFilter, setIsEnabledFilter] = useState("");

  // Handle direct "New" action from Navbar
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("action") === "new") {
      handleCreate();
    }
  }, [location.search]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const {
    data: offersData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["productOffers", page, rowsPerPage, debouncedSearch, appTypeFilter, isEnabledFilter],
    queryFn: () => fetchOffers(page, rowsPerPage, debouncedSearch, appTypeFilter, isEnabledFilter),
    keepPreviousData: true,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteOffer,
    onSuccess: () => {
      queryClient.invalidateQueries(["productOffers"]);
      setSnackbar({
        open: true,
        message: "Offer deleted successfully!",
        severity: "success",
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error deleting offer: ${error.message}`,
        severity: "error",
      });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: toggleOfferStatus,
    onSuccess: () => {
      queryClient.invalidateQueries(["productOffers"]);
      setSnackbar({
        open: true,
        message: "Offer status updated!",
        severity: "success",
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error updating status: ${error.message}`,
        severity: "error",
      });
    },
  });

  const handleCreate = () => {
    setEditingOffer(null);
    setDialogOpen(true);
  };

  const handleEdit = (offer) => {
    setEditingOffer(offer);
    setDialogOpen(true);
  };

  const handleDelete = (id) => {
    setOfferToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (offerToDelete) {
      deleteMutation.mutate(offerToDelete);
      setDeleteConfirmOpen(false);
      setOfferToDelete(null);
    }
  };

  const handleToggle = (offer) => {
    toggleMutation.mutate({ id: offer._id, isEnabled: !offer.isEnabled });
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error loading offers: {error.message}
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          border: `1px solid ${theme.palette.divider}`,
          padding: theme.spacing(4),
          borderRadius: 2,
          flexDirection: isMobile ? "column" : "row",
          gap: 2,
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="h4" fontWeight={"bold"} gutterBottom>
              Product Offers Management
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Manage dynamic landing pages for product offers.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          size={isMobile ? "small" : "medium"}
        >
          Create Offer
        </Button>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search offers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>App Type</InputLabel>
              <Select
                value={appTypeFilter}
                onChange={(e) => setAppTypeFilter(e.target.value)}
                label="App Type"
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="hr">HR</MenuItem>
                <MenuItem value="files">Files</MenuItem>
                <MenuItem value="esign">Esign</MenuItem>
                <MenuItem value="projects">Projects</MenuItem>
                <MenuItem value="chat">Chat</MenuItem>
                <MenuItem value="worksuite">Worksuite</MenuItem>
                <MenuItem value="campaigns">Campaigns</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={isEnabledFilter}
                onChange={(e) => setIsEnabledFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" fontWeight="medium">
          {isLoading ? "Searching..." : `Showing ${offersData?.offers?.length || 0} of ${offersData?.totalOffers || 0} offers`}
        </Typography>
      </Box>

      <Card
        sx={{
          backgroundColor: "transparent",
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "none",
        }}
      >
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>App Type</TableCell>
                  <TableCell>Offer Slug</TableCell>
                  <TableCell>Hero Title</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : offersData?.offers?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body1" color="text.secondary">
                        No offers found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  offersData?.offers?.map((offer) => (
                    <TableRow key={offer._id}>
                      <TableCell>{offer.name}</TableCell>
                      <TableCell>
                        <Chip
                          label={offer.appType?.toUpperCase() || 'N/A'}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{offer.slug}</TableCell>
                      <TableCell>{offer.hero?.title}</TableCell>
                      <TableCell>
                        {new Date(offer.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={offer.isEnabled ? "Active" : "Inactive"}
                          size="small"
                          color={offer.isEnabled ? "success" : "default"}
                          variant={offer.isEnabled ? "filled" : "outlined"}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => window.open(`https://clikkle.com/offers/${offer.slug}`, "_blank")}
                            color="info"
                            title="Open in new tab"
                          >
                           <ArrowUpward />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleToggle(offer)}
                            color={offer.isEnabled ? "success" : "default"}
                          >
                            {offer.isEnabled ? <ViewIcon /> : <VisibilityOffIcon />}
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(offer)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(offer._id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Pagination */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={3}
      >
        <RowPerPage
          rowsPerPage={rowsPerPage}
          onRowsChange={(newRowsPerPage) => {
            setRowsPerPage(newRowsPerPage);
            setPage(1);
          }}
        />
        <Pagination
          count={offersData?.totalOffers || 0}
          page={page - 1}
          rowsPerPage={rowsPerPage}
          onPageChange={(newPage) => setPage(newPage + 1)}
        />
      </Box>

      <ProductOfferForm
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingOffer(null);
        }}
        offer={editingOffer}
        onSuccess={() => {
          queryClient.invalidateQueries(["productOffers"]);
          setDialogOpen(false);
          setEditingOffer(null);
          setSnackbar({
            open: true,
            message: editingOffer
              ? "Offer updated successfully!"
              : "Offer created successfully!",
            severity: "success",
          });
        }}
      />

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Delete Offer?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this offer? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductOffers;
